import { useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import { fmtDate } from '../events/util';

// Logo 3D de Tukipass (firma al pie de la tarjeta). Bundleado en /public/assets.
const TUKIPASS_LOGO = '/assets/tukipass.png';

// Carga una imagen sin "tintar" el canvas (crossOrigin). Si falla, resuelve null.
function loadImg(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Dibuja texto centrado con corte de línea; devuelve la Y final.
function drawWrapped(ctx, text, cx, y, maxW, lineH) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  let line = '';
  const lines = [];
  for (const w of words) {
    const test = line ? line + ' ' + w : w;
    if (ctx.measureText(test).width > maxW && line) { lines.push(line); line = w; }
    else line = test;
  }
  if (line) lines.push(line);
  for (const ln of lines) { ctx.fillText(ln, cx, y); y += lineH; }
  return y;
}

// Pinta el chip del logo del venue en el centro de un canvas de QR ya renderizado.
function drawQrChip(canvas, logo) {
  return new Promise((resolve) => {
    if (!logo) return resolve();
    const ctx = canvas.getContext('2d');
    const dim = canvas.width;
    const chip = Math.round(dim * 0.24);
    const x = (dim - chip) / 2, y = (dim - chip) / 2;
    ctx.fillStyle = '#F5F1EA'; ctx.fillRect(x - 6, y - 6, chip + 12, chip + 12);
    ctx.fillStyle = '#0A0A0A'; ctx.fillRect(x, y, chip, chip);
    const img = new Image();
    img.onload = () => { ctx.drawImage(img, x + chip * 0.16, y + chip * 0.13, chip * 0.68, chip * 0.74); resolve(); };
    img.onerror = () => resolve();
    img.src = logo;
  });
}

// Tarjeta de entrada con QR. Si recibe `transportValue`, compone UNA sola tarjeta
// (y un solo PNG) con los DOS QR etiquetados adentro: INGRESO (puerta) y TRASLADO
// (micro). Así no se descargan dos imágenes idénticas sin distinción.
// brightnessHint: aviso al comprador de subir el brillo. Va por default porque
// medimos que el CONTRASTE pesa más que cualquier optimización del escáner: con
// una pantalla muy oscura no lo lee ningún tamaño de análisis. Se apaga en las
// pantallas internas del panel, donde no hay un comprador del otro lado.
export default function TicketQR({
  value, size = 240, logo, filename, eventName, attendeeName, date, lotName,
  transportValue = null, ingresoLabel = 'INGRESO · PUERTA', transportLabel = 'TRASLADO',
  brightnessHint = true,
}) {
  const ref = useRef(null);
  const ref2 = useRef(null);
  const dual = !!transportValue;

  // QR de ingreso (siempre) en pantalla
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !value) return;
    QRCode.toCanvas(canvas, value, { width: size, margin: 2, errorCorrectionLevel: 'H', color: { dark: '#0A0A0A', light: '#F5F1EA' } }, () => drawQrChip(canvas, logo));
  }, [value, size, logo]);

  // QR de traslado (si aplica) en pantalla
  useEffect(() => {
    const canvas = ref2.current;
    if (!canvas || !transportValue) return;
    QRCode.toCanvas(canvas, transportValue, { width: size, margin: 2, errorCorrectionLevel: 'H', color: { dark: '#0A0A0A', light: '#F5F1EA' } }, () => drawQrChip(canvas, logo));
  }, [transportValue, size, logo]);

  // Dibuja un panel etiquetado (label + placa clara + QR + chip). Devuelve la Y de abajo.
  function drawLabeledQr(ctx, W, top, label, qrCanvas, homeImg) {
    ctx.fillStyle = '#C8102E';
    ctx.font = '700 42px "Unbounded", system-ui, sans-serif';
    ctx.fillText(label, W / 2, top + 42);
    const panel = 560, qr = 470;
    const px = (W - panel) / 2, py = top + 78;
    roundRect(ctx, px, py, panel, panel, 26); ctx.fillStyle = '#F5F1EA'; ctx.fill();
    if (qrCanvas) {
      const qx = px + (panel - qr) / 2, qy = py + (panel - qr) / 2;
      ctx.drawImage(qrCanvas, qx, qy, qr, qr);
      if (homeImg) {
        const chip = Math.round(qr * 0.22);
        const cxp = qx + (qr - chip) / 2, cyp = qy + (qr - chip) / 2;
        ctx.fillStyle = '#F5F1EA'; ctx.fillRect(cxp - 8, cyp - 8, chip + 16, chip + 16);
        ctx.fillStyle = '#0A0A0A'; ctx.fillRect(cxp, cyp, chip, chip);
        ctx.drawImage(homeImg, cxp + chip * 0.16, cyp + chip * 0.13, chip * 0.68, chip * 0.74);
      }
    }
    return py + panel;
  }

  const download = async () => {
    try {
      // Lienzo de trabajo generoso; al final recortamos al alto real del contenido.
      const W = 1080, H = 3400;
      const card = document.createElement('canvas');
      card.width = W; card.height = H;
      const ctx = card.getContext('2d');
      ctx.fillStyle = '#000000'; ctx.fillRect(0, 0, W, H);
      ctx.textAlign = 'center';
      try { if (document.fonts) await document.fonts.ready; } catch {}

      const mkQr = (v, w) => QRCode.toCanvas(document.createElement('canvas'), v, { width: w, margin: 1, errorCorrectionLevel: 'H', color: { dark: '#0A0A0A', light: '#F5F1EA' } }).then(c => c).catch(() => null);
      const [homeImg, tukiImg, qr1, qr2] = await Promise.all([
        loadImg(logo), loadImg(TUKIPASS_LOGO),
        mkQr(value, dual ? 470 : 620),
        dual ? mkQr(transportValue, 470) : Promise.resolve(null),
      ]);

      let cy = dual ? 110 : 140;

      if (homeImg) {
        const maxW = 460, maxH = dual ? 170 : 210;
        const s = Math.min(maxW / homeImg.width, maxH / homeImg.height);
        const w = homeImg.width * s, h = homeImg.height * s;
        ctx.drawImage(homeImg, (W - w) / 2, cy, w, h);
        cy += h + (dual ? 60 : 120);
      } else { cy = dual ? 180 : 250; }

      ctx.fillStyle = '#ffffff';
      ctx.font = '700 60px "Unbounded", system-ui, sans-serif';
      cy = drawWrapped(ctx, (eventName || '').toUpperCase(), W / 2, cy, W - 150, 72);

      const dateText = date ? fmtDate(date) : '';
      if (dateText) {
        cy += 6;
        ctx.fillStyle = '#9A9A9A';
        ctx.font = '500 34px "DM Sans", system-ui, sans-serif';
        const cap = dateText.charAt(0).toUpperCase() + dateText.slice(1);
        ctx.fillText(cap, W / 2, cy);
        cy += 18;
      }
      cy += dual ? 24 : 34;

      if (dual) {
        // Dos QR etiquetados, uno arriba del otro, en la MISMA tarjeta.
        cy = drawLabeledQr(ctx, W, cy, ingresoLabel, qr1, homeImg) + 60;
        cy = drawLabeledQr(ctx, W, cy, transportLabel, qr2, homeImg) + 70;
      } else {
        const panel = 640, qr = 540;
        const px = (W - panel) / 2, py = cy;
        roundRect(ctx, px, py, panel, panel, 28); ctx.fillStyle = '#F5F1EA'; ctx.fill();
        if (qr1) {
          const qx = px + (panel - qr) / 2, qy = py + (panel - qr) / 2;
          ctx.drawImage(qr1, qx, qy, qr, qr);
          if (homeImg) {
            const chip = Math.round(qr * 0.22);
            const cxp = qx + (qr - chip) / 2, cyp = qy + (qr - chip) / 2;
            ctx.fillStyle = '#F5F1EA'; ctx.fillRect(cxp - 8, cyp - 8, chip + 16, chip + 16);
            ctx.fillStyle = '#0A0A0A'; ctx.fillRect(cxp, cyp, chip, chip);
            ctx.drawImage(homeImg, cxp + chip * 0.16, cyp + chip * 0.13, chip * 0.68, chip * 0.74);
          }
        }
        cy = py + panel + 78;
      }

      if (attendeeName) {
        ctx.fillStyle = '#ffffff';
        ctx.font = '700 52px "Unbounded", system-ui, sans-serif';
        cy = drawWrapped(ctx, attendeeName, W / 2, cy, W - 150, 62);
      }
      if (lotName) {
        cy += 22;
        ctx.fillStyle = '#9A9A9A';
        ctx.font = '500 64px "DM Sans", system-ui, sans-serif';
        ctx.fillText(lotName, W / 2, cy);
        cy += 28;
      }
      cy += 40;
      ctx.fillStyle = '#C8102E';
      ctx.font = '600 26px "DM Sans", system-ui, sans-serif';
      if (dual) {
        ctx.fillText(`Ingreso ${value}  ·  Traslado ${transportValue}`, W / 2, cy);
      } else {
        ctx.fillText(value, W / 2, cy);
      }

      // Pie de TukiPro justo debajo del contenido (no pegado al fondo).
      let bottom = cy + 40;
      if (tukiImg) {
        const tw = 360, ts = tw / tukiImg.width, th = tukiImg.height * ts;
        ctx.drawImage(tukiImg, (W - tw) / 2, cy + 70, tw, th);
        bottom = cy + 70 + th;
      }
      // Recorte al alto real (elimina el espacio negro sobrante abajo).
      const outH = Math.min(H, Math.round(bottom + 90));
      const out = document.createElement('canvas');
      out.width = W; out.height = outH;
      out.getContext('2d').drawImage(card, 0, 0);

      const a = document.createElement('a');
      a.href = out.toDataURL('image/png');
      a.download = filename || `entrada-${value}.png`;
      a.click();
    } catch {
      const canvas = ref.current;
      if (!canvas) return;
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = filename || `entrada-${value}.png`;
      a.click();
    }
  };

  const QrBox = ({ canvasRef, label }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      {label && <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, letterSpacing: '0.14em', fontSize: 13, color: 'var(--red-400)' }}>{label}</div>}
      <div style={{ background: '#F5F1EA', padding: 14, borderRadius: 4 }}>
        <canvas ref={canvasRef} width={size} height={size} style={{ display: 'block', width: size, height: size }} />
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
      {dual ? (
        <>
          <QrBox canvasRef={ref} label={ingresoLabel} />
          <QrBox canvasRef={ref2} label={transportLabel} />
        </>
      ) : (
        <QrBox canvasRef={ref} />
      )}
      {brightnessHint && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, maxWidth: 300,
          background: 'rgba(212,175,55,0.10)', border: '1px solid rgba(212,175,55,0.35)',
          borderRadius: 6, padding: '9px 12px',
        }}>
          <span style={{ fontSize: 15, lineHeight: 1 }}>☀</span>
          <span style={{ fontSize: 12.5, color: 'var(--ink-800)', lineHeight: 1.4 }}>
            Subí el <b style={{ color: '#fff' }}>brillo de tu pantalla</b> al máximo para entrar más rápido.
          </span>
        </div>
      )}
      <button className="lb-btn lb-btn--white lb-btn--sm" onClick={download}>
        {dual ? 'Descargar entrada (2 QR) ↓' : 'Descargar entrada ↓'}
      </button>
    </div>
  );
}
